import type { TemplateRegistration, TemplateData } from './types';
import { sanitizeText, sanitizeSeoText, escapeAttr } from './sanitize';
import { getIconSvg, renderStars, buildFontsUrl, buildNavLinks, NavDataFlags, truncateAtSentence } from './helpers';

export const skyCanvas: TemplateRegistration = {
  meta: {
    id: 'sky-canvas',
    name: 'Sky Canvas',
    category: 'Minimal',
    description: 'Airy sky-blue accents with generous whitespace',
    previewImage: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=800&q=80',
    supportedFeatures: ['contact-form', 'phone-display', 'address-map', 'business-hours', 'about-section', 'services-list', 'testimonials', 'google-reviews', 'social-links', 'faq', 'price-list', 'promotions', 'photo-gallery'],
    defaultEnabledFeatures: ['contact-form', 'phone-display', 'about-section', 'services-list', 'testimonials'],
  },
  render(data: TemplateData, enabledFeatures: Set<string>): string {
    const safeName = sanitizeText(data.businessName);
    const safeHeadline = sanitizeText(data.headline);
    const safeAbout = sanitizeText(data.aboutText);
    const safeSeoTitle = sanitizeSeoText(data.seo.title, 70);
    const safeSeoDesc = sanitizeSeoText(data.seo.description, 160);

    const phone = data.businessData?.phone || null;
    const contactEmail = data.businessData?.contactEmail || null;
    const rating = data.businessData?.rating || null;
    const reviewCount = data.businessData?.reviewCount || 0;
    const businessHours = data.businessData?.businessHours || [];
    const photos = data.businessData?.photos || [];
    const address = data.businessData?.address || '';
    const googleMapsUrl = data.businessData?.googleMapsUrl || null;
    const heroImage = photos[0] || null;
    const galleryImages = photos.slice(1, 6);

    const navDataFlags: NavDataFlags = {
      hasServices: data.services.length > 0,
      hasTestimonials: data.testimonials.length > 0,
      hasGalleryPhotos: galleryImages.length > 0,
      hasFaq: !!(data.faq && data.faq.length > 0),
      hasPriceList: !!(data.priceList && data.priceList.length > 0),
    };
    const navLinks = buildNavLinks(enabledFeatures, navDataFlags);
    const fontsUrl = buildFontsUrl(['Plus Jakarta Sans', 'Figtree']);

    const sections: string[] = [];

    // --- Google Reviews ---
    if (enabledFeatures.has('google-reviews') && rating) {
      sections.push(`
      <section class="google-reviews">
        <div class="container">
          <div class="review-badge">
            <div class="review-stars">${renderStars(Math.round(rating))}</div>
            <span class="review-text">${rating.toFixed(1)} out of 5 &mdash; ${reviewCount} Google reviews</span>
          </div>
        </div>
      </section>`);
    }

    // --- Promotions ---
    if (enabledFeatures.has('promotions') && data.promotion) {
      const promoTitle = sanitizeText(data.promotion.title);
      const promoDesc = sanitizeText(data.promotion.description);
      const promoDiscount = sanitizeText(data.promotion.discount);
      sections.push(`
      <section class="promo-banner">
        <div class="container">
          <div class="promo-inner">
            <span class="promo-badge">${promoDiscount}</span>
            <div class="promo-content">
              <strong>${promoTitle}</strong>
              <span>${promoDesc}</span>
            </div>
          </div>
        </div>
      </section>`);
    }

    // --- About ---
    if (enabledFeatures.has('about-section')) {
      sections.push(`
      <section id="about" class="section section-sky">
        <div class="container">
          <h2 class="section-title">About Us</h2>
          <div class="about-decorated">
            <p class="about-text">${safeAbout}</p>
          </div>
        </div>
      </section>`);
    }

    // --- Services ---
    if (enabledFeatures.has('services-list') && data.services.length > 0) {
      const serviceCards = data.services.map(s => {
        const sName = sanitizeText(s.name);
        const sDesc = sanitizeText(s.description);
        return `
          <div class="service-card">
            <div class="service-icon">${getIconSvg(s.icon || 'star')}</div>
            <h3>${sName}</h3>
            <p>${sDesc}</p>
            ${s.price ? `<span class="service-price">${sanitizeText(s.price)}</span>` : ''}
          </div>`;
      }).join('');
      sections.push(`
      <section id="services" class="section section-white">
        <div class="container">
          <h2 class="section-title">Our Services</h2>
          <div class="services-masonry">${serviceCards}</div>
        </div>
      </section>`);
    }

    // --- Photo Gallery ---
    if (enabledFeatures.has('photo-gallery') && galleryImages.length > 0) {
      const imgs = galleryImages.map(url => {
        return `<div class="gallery-item"><img src="${escapeAttr(url)}" alt="${safeName}" loading="lazy"></div>`;
      }).join('');
      sections.push(`
      <section id="gallery" class="section section-sky">
        <div class="container">
          <h2 class="section-title">Gallery</h2>
          <div class="gallery-grid">${imgs}</div>
        </div>
      </section>`);
    }

    // --- Testimonials ---
    if (enabledFeatures.has('testimonials') && data.testimonials.length > 0) {
      const cards = data.testimonials.map(t => {
        const tName = sanitizeText(t.name);
        const tContent = sanitizeText(t.content);
        const tRole = t.role ? sanitizeText(t.role) : '';
        return `
          <div class="testimonial-card">
            <p class="testimonial-body">&ldquo;${tContent}&rdquo;</p>
            <div class="testimonial-author">
              <strong>${tName}</strong>
              ${tRole ? `<span>${tRole}</span>` : ''}
              ${t.rating ? `<div class="testimonial-stars">${renderStars(t.rating)}</div>` : ''}
            </div>
          </div>`;
      }).join('');
      sections.push(`
      <section id="testimonials" class="section section-white">
        <div class="container">
          <h2 class="section-title">What People Say</h2>
          <div class="testimonials-carousel">${cards}</div>
        </div>
      </section>`);
    }

    // --- FAQ ---
    if (enabledFeatures.has('faq') && data.faq && data.faq.length > 0) {
      const items = data.faq.map(f => {
        const q = sanitizeText(f.question);
        const a = sanitizeText(f.answer);
        return `
          <div class="faq-item">
            <h3 class="faq-q">${q}</h3>
            <p class="faq-a">${a}</p>
          </div>`;
      }).join('');
      sections.push(`
      <section id="faq" class="section section-sky">
        <div class="container">
          <h2 class="section-title">FAQ</h2>
          <div class="faq-list">${items}</div>
        </div>
      </section>`);
    }

    // --- Price List ---
    if (enabledFeatures.has('price-list') && data.priceList && data.priceList.length > 0) {
      const rows = data.priceList.map(p => {
        const pName = sanitizeText(p.name);
        const pDesc = sanitizeText(p.description);
        const pPrice = sanitizeText(p.price);
        return `
          <div class="price-row">
            <div class="price-info">
              <h3>${pName}</h3>
              <p>${pDesc}</p>
            </div>
            <span class="price-amount">${pPrice}</span>
          </div>`;
      }).join('');
      sections.push(`
      <section id="pricing" class="section section-white">
        <div class="container">
          <h2 class="section-title">Pricing</h2>
          <div class="price-list">${rows}</div>
        </div>
      </section>`);
    }

    // --- Business Hours ---
    if (enabledFeatures.has('business-hours') && businessHours.length > 0) {
      const hoursList = businessHours.map(h => `<li>${sanitizeText(h)}</li>`).join('');
      sections.push(`
      <section class="section section-sky">
        <div class="container">
          <h2 class="section-title">Hours</h2>
          <ul class="hours-list">${hoursList}</ul>
        </div>
      </section>`);
    }

    // --- Contact ---
    if (enabledFeatures.has('contact-form')) {
      sections.push(`
      <section id="contact" class="section section-white">
        <div class="container">
          <h2 class="section-title">Get In Touch</h2>
          <div class="contact-stacked">
            <div class="contact-map-area">
              <div class="contact-info">
                ${enabledFeatures.has('phone-display') && phone ? `<div class="contact-item">${getIconSvg('phone')}<a href="tel:${escapeAttr(phone)}">${sanitizeText(phone)}</a></div>` : ''}
                ${enabledFeatures.has('address-map') && address ? `<div class="contact-item">${getIconSvg('map-pin')}<span>${sanitizeText(address)}</span></div>` : ''}
                ${enabledFeatures.has('address-map') && googleMapsUrl ? `<a class="map-link" href="${escapeAttr(googleMapsUrl)}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>` : ''}
              </div>
            </div>
            <form class="contact-form" ${contactEmail ? `action="https://formsubmit.co/${escapeAttr(contactEmail)}" method="POST"` : 'onsubmit="return false;"'}>
              <input type="text" placeholder="Your Name" required>
              <input type="email" placeholder="Your Email" required>
              <textarea placeholder="Message" rows="4" required></textarea>
              <button type="submit">Send Message</button>
            </form>
          </div>
        </div>
      </section>`);
    }

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${safeSeoTitle}</title>
  <meta name="description" content="${safeSeoDesc}">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${fontsUrl}" rel="stylesheet">
  <style>
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    :root {
      --primary: #0F172A; --bg: #FFFFFF; --bg-alt: #E0F2FE; --accent: #0EA5E9;
      --text: #0F172A; --text-muted: #64748B;
      --font-heading: 'Plus Jakarta Sans', sans-serif; --font-body: 'Figtree', sans-serif;
      --shadow-sm: 0 1px 3px rgba(15,23,42,.06); --shadow-md: 0 4px 16px rgba(15,23,42,.08);
    }
    html { scroll-behavior: smooth; }
    body { font-family: var(--font-body); background: var(--bg); color: var(--text); line-height: 1.7; }
    .container { max-width: 1100px; margin: 0 auto; padding: 0 32px; }
    a { color: var(--accent); text-decoration: none; transition: opacity .2s; }
    a:hover { opacity: .8; }

    /* NAV */
    .navbar { position: fixed; top: 0; left: 0; right: 0; z-index: 100; background: rgba(255,255,255,.96); backdrop-filter: blur(12px); border-bottom: 1px solid #E2E8F0; }
    .nav-inner { display: flex; align-items: center; justify-content: space-between; padding: 16px 32px; max-width: 1100px; margin: 0 auto; }
    .nav-brand { font-family: var(--font-heading); font-size: 1.3rem; color: var(--text); font-weight: 700; }
    .nav-links { display: flex; gap: 32px; list-style: none; }
    .nav-links a { color: var(--text-muted); font-size: .85rem; font-weight: 500; letter-spacing: 0.3px; }
    .nav-links a:hover { color: var(--accent); }
    .nav-toggle { display: none; background: none; border: none; cursor: pointer; }
    .nav-toggle span { display: block; width: 24px; height: 2px; background: var(--text); margin: 5px 0; transition: .3s; border-radius: 2px; }

    /* HERO - angled sections with clip-path */
    .hero { position: relative; min-height: 100vh; display: flex; align-items: center; overflow: hidden; background: var(--bg-alt); clip-path: polygon(0 0, 100% 0, 100% 85%, 0 100%); margin-bottom: -60px; padding-bottom: 80px; }
    .hero-bg { position: absolute; inset: 0; background-size: cover; background-position: center; opacity: .1; }
    .hero-overlay { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(224,242,254,.92), var(--bg-alt)); }
    .hero-content { position: relative; z-index: 2; padding: 160px 32px 120px; max-width: 1100px; margin: 0 auto; width: 100%; }
    .hero h1 { font-family: var(--font-heading); font-size: clamp(2.6rem, 6vw, 4.4rem); line-height: 1.12; margin-bottom: 24px; font-weight: 800; color: var(--text); }
    .hero-sub { font-size: 1.1rem; color: var(--text-muted); max-width: 560px; margin-bottom: 44px; line-height: 1.8; }
    .hero-cta { display: inline-block; padding: 16px 40px; background: var(--accent); color: #fff; font-family: var(--font-heading); font-size: .95rem; font-weight: 700; border: none; border-radius: 10px; cursor: pointer; transition: transform .2s, box-shadow .2s; box-shadow: 0 4px 16px rgba(14,165,233,.25); }
    .hero-cta:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(14,165,233,.3); opacity: 1; color: #fff; }

    /* SECTIONS */
    .section { padding: 100px 0; }
    .section-white { background: var(--bg); }
    .section-sky { background: var(--bg-alt); }
    .section-title { font-family: var(--font-heading); font-size: clamp(1.6rem, 3.5vw, 2.4rem); font-weight: 800; margin-bottom: 48px; color: var(--text); }

    /* ABOUT - centered with decorative border lines */
    .about-decorated { max-width: 720px; margin: 0 auto; text-align: center; position: relative; padding: 48px 0; }
    .about-decorated::before { content: ''; position: absolute; top: 0; left: 50%; transform: translateX(-50%); width: 120px; height: 3px; background: linear-gradient(90deg, transparent, var(--accent), transparent); }
    .about-decorated::after { content: ''; position: absolute; bottom: 0; left: 50%; transform: translateX(-50%); width: 120px; height: 3px; background: linear-gradient(90deg, transparent, var(--accent), transparent); }
    .about-text { font-size: 1.1rem; color: var(--text-muted); line-height: 1.9; }

    /* SERVICES - masonry (CSS columns) */
    .services-masonry { columns: 2; column-gap: 28px; }
    .service-card { background: #fff; padding: 36px 28px; border-radius: 12px; border: 1px solid #E2E8F0; box-shadow: var(--shadow-sm); transition: transform .3s, box-shadow .3s; break-inside: avoid; margin-bottom: 28px; display: inline-block; width: 100%; }
    .service-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-md); }
    .service-icon { margin-bottom: 16px; }
    .icon-char { font-size: 28px; line-height: 1; color: var(--accent); }
    .service-card h3 { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; margin-bottom: 8px; color: var(--text); }
    .service-card p { color: var(--text-muted); font-size: .9rem; line-height: 1.7; }
    .service-price { display: inline-block; margin-top: 12px; font-weight: 700; font-size: 1.05rem; color: var(--accent); }

    /* GALLERY */
    .gallery-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
    .gallery-item img { width: 100%; height: 240px; object-fit: cover; display: block; border-radius: 10px; box-shadow: var(--shadow-sm); transition: transform .3s; }
    .gallery-item img:hover { transform: scale(1.02); }

    /* TESTIMONIALS - horizontal scroll-snap carousel */
    .testimonials-carousel { display: flex; overflow-x: auto; scroll-snap-type: x mandatory; gap: 24px; padding-bottom: 16px; -webkit-overflow-scrolling: touch; }
    .testimonials-carousel::-webkit-scrollbar { height: 6px; }
    .testimonials-carousel::-webkit-scrollbar-track { background: #E2E8F0; border-radius: 3px; }
    .testimonials-carousel::-webkit-scrollbar-thumb { background: var(--accent); border-radius: 3px; }
    .testimonial-card { background: var(--bg-alt); padding: 32px; border-radius: 12px; border: 1px solid #BAE6FD; scroll-snap-align: start; min-width: 320px; max-width: 400px; flex-shrink: 0; }
    .testimonial-body { font-size: 1rem; font-style: italic; color: var(--text-muted); line-height: 1.8; margin-bottom: 16px; }
    .testimonial-author strong { display: block; color: var(--text); font-size: .95rem; }
    .testimonial-author span { color: var(--text-muted); font-size: .85rem; }
    .testimonial-stars { margin-top: 8px; }
    .star { color: #CBD5E1; font-size: 1rem; }
    .star.filled { color: var(--accent); }

    /* FAQ */
    .faq-list { max-width: 760px; }
    .faq-item { padding: 28px 0; border-bottom: 1px solid #E2E8F0; }
    .faq-q { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; margin-bottom: 10px; color: var(--text); }
    .faq-a { color: var(--text-muted); line-height: 1.8; }

    /* PRICE LIST */
    .price-list { max-width: 760px; }
    .price-row { display: flex; justify-content: space-between; align-items: flex-start; padding: 28px 0; border-bottom: 1px solid #E2E8F0; gap: 24px; }
    .price-info h3 { font-family: var(--font-heading); font-size: 1.1rem; font-weight: 700; margin-bottom: 4px; color: var(--text); }
    .price-info p { color: var(--text-muted); font-size: .9rem; }
    .price-amount { font-family: var(--font-heading); font-size: 1.4rem; color: var(--accent); white-space: nowrap; font-weight: 700; }

    /* HOURS */
    .hours-list { list-style: none; max-width: 420px; }
    .hours-list li { padding: 14px 0; border-bottom: 1px solid rgba(14,165,233,.15); color: var(--text-muted); font-size: .95rem; }

    /* GOOGLE REVIEWS */
    .google-reviews { background: var(--accent); padding: 18px 0; }
    .review-badge { display: flex; align-items: center; justify-content: center; gap: 12px; flex-wrap: wrap; }
    .review-badge .star, .review-badge .star.filled { color: #fff; font-size: 1.2rem; }
    .review-text { color: #fff; font-weight: 700; font-size: .95rem; }

    /* PROMO */
    .promo-banner { background: var(--bg-alt); padding: 20px 0; border-bottom: 1px solid #BAE6FD; }
    .promo-inner { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
    .promo-badge { background: var(--accent); color: #fff; font-family: var(--font-heading); padding: 8px 22px; font-size: 1rem; font-weight: 700; border-radius: 8px; }
    .promo-content strong { display: block; margin-bottom: 2px; color: var(--text); }
    .promo-content span { color: var(--text-muted); font-size: .9rem; }

    /* CONTACT - map + form stacked */
    .contact-stacked { max-width: 700px; margin: 0 auto; }
    .contact-map-area { margin-bottom: 32px; background: var(--bg-alt); border-radius: 12px; padding: 32px; display: flex; flex-direction: column; gap: 16px; align-items: center; }
    .contact-info { display: flex; flex-direction: column; gap: 16px; width: 100%; }
    .contact-item { display: flex; align-items: center; gap: 12px; color: var(--text-muted); }
    .contact-item .icon-char { color: var(--accent); flex-shrink: 0; font-size: 22px; line-height: 1; }
    .contact-item a { color: var(--text); }
    .map-link { display: inline-block; margin-top: 8px; font-weight: 600; font-size: .85rem; letter-spacing: 0.5px; color: var(--accent); }
    .contact-form { display: flex; flex-direction: column; gap: 16px; }
    .contact-form input, .contact-form textarea { background: #fff; border: 1px solid #E2E8F0; border-radius: 10px; color: var(--text); padding: 16px 18px; font-family: var(--font-body); font-size: .95rem; outline: none; transition: border-color .2s; }
    .contact-form input:focus, .contact-form textarea:focus { border-color: var(--accent); }
    .contact-form button { padding: 16px; background: var(--accent); color: #fff; font-family: var(--font-heading); font-size: .95rem; font-weight: 700; border: none; border-radius: 10px; cursor: pointer; transition: transform .2s, box-shadow .2s; }
    .contact-form button:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(14,165,233,.25); }

    /* FOOTER */
    .footer { background: var(--primary); padding: 48px 0; text-align: center; }
    .footer p { color: #94A3B8; font-size: .85rem; }

    /* RESPONSIVE */
    @media (max-width: 768px) {
      .nav-links { display: none; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: rgba(255,255,255,.98); border-bottom: 1px solid #E2E8F0; padding: 16px 32px; gap: 16px; }
      .nav-links.show { display: flex; }
      .nav-toggle { display: block; }
      .services-masonry { columns: 1; }
      .gallery-grid { grid-template-columns: repeat(2, 1fr); }
      .testimonial-card { min-width: 260px; }
      .price-row { flex-direction: column; gap: 8px; }
      .container { padding: 0 24px; }
    }
    @media (max-width: 480px) {
      .gallery-grid { grid-template-columns: 1fr; }
      .hero-content { padding: 120px 20px 80px; }
    }
  </style>
</head>
<body>
  <nav class="navbar">
    <div class="nav-inner">
      <a href="#" class="nav-brand">${safeName}</a>
      <button class="nav-toggle" aria-label="Toggle menu" onclick="this.parentElement.querySelector('.nav-links').classList.toggle('show')">
        <span></span><span></span><span></span>
      </button>
      <ul class="nav-links">
        ${navLinks.map(l => `<li><a href="${l.href}">${l.label}</a></li>`).join('')}
      </ul>
    </div>
  </nav>

  <section class="hero">
    ${heroImage ? `<div class="hero-bg" style="background-image:url('${escapeAttr(heroImage)}')"></div>` : ''}
    <div class="hero-overlay"></div>
    <div class="hero-content">
      <h1>${safeHeadline}</h1>
      <p class="hero-sub">${truncateAtSentence(safeAbout)}</p>
      ${enabledFeatures.has('contact-form') ? `<a href="#contact" class="hero-cta">Get Started</a>` : ''}
    </div>
  </section>

  ${sections.join('\n')}

  <footer class="footer">
    <div class="container">
      <p>${safeName}</p>
    </div>
  </footer>
</body>
</html>`;
  }
};
